import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import * as React from 'react'

import { cn } from '@/utils'

const TooltipProvider = TooltipPrimitive.Provider
const TooltipRoot = TooltipPrimitive.Root
const TooltipTrigger = TooltipPrimitive.Trigger
const TooltipPortal = TooltipPrimitive.Portal
const TooltipArrow = TooltipPrimitive.Arrow

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'z-50 overflow-hidden rounded-md border bg-popover px-2 py-1 text-xs text-popover-foreground shadow-md',
      'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out',
      'data-[state=delayed-open]:fade-in-0 data-[state=closed]:fade-out-0',
      'data-[state=delayed-open]:zoom-in-95 data-[state=closed]:zoom-out-95',
      'data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1',
      'data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1',
      className,
    )}
    {...props}
  />
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

// TooltipContent variant whose border wraps the entire silhouette,
// including a TooltipArrow caret. `border` + `box-shadow` only follow
// the rectangular content box and leave the arrow naked; replacing
// them with a stacked `filter: drop-shadow(...)` outlines the combined
// popover + arrow shape with a continuous 1px hairline plus a soft
// depth shadow. `overflow-visible` keeps the arrow from being clipped
// by the base TooltipContent's `overflow-hidden`.
// Shared by any floating surface with an arrow (tooltip, popover), not just tooltips.
export const BORDERED_SURFACE_FILTER =
  'drop-shadow(1px 0 0 hsl(var(--border))) drop-shadow(-1px 0 0 hsl(var(--border))) drop-shadow(0 1px 0 hsl(var(--border))) drop-shadow(0 -1px 0 hsl(var(--border))) drop-shadow(0 2px 4px rgb(0 0 0 / 0.08))'

const BorderedTooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, children, style, ...props }, ref) => (
  <TooltipContent
    ref={ref}
    className={cn('overflow-visible border-0 shadow-none', className)}
    style={{ filter: BORDERED_SURFACE_FILTER, ...style }}
    {...props}
  >
    {children}
    <TooltipArrow className="fill-popover" />
  </TooltipContent>
))
BorderedTooltipContent.displayName = 'BorderedTooltipContent'

type TooltipSide = 'top' | 'right' | 'bottom' | 'left'

interface TooltipProps {
  content: React.ReactNode
  children: React.ReactNode
  side?: TooltipSide
  delayDuration?: number
  className?: string
}

// Convenience wrapper for the common case — a single trigger and a single
// short string label. For richer content or controlled state, drop down
// to TooltipRoot / TooltipTrigger / TooltipContent directly.
function Tooltip({
  content,
  children,
  side = 'bottom',
  delayDuration = 200,
  className,
}: TooltipProps) {
  if (content === null || content === undefined || content === '') {
    return <>{children}</>
  }
  return (
    <TooltipRoot delayDuration={delayDuration}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipPortal>
        <TooltipContent side={side} className={className}>
          {content}
        </TooltipContent>
      </TooltipPortal>
    </TooltipRoot>
  )
}

export {
  BorderedTooltipContent,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
}
